import { useState } from "react";
import { supabase } from "../services/supabase";
import { validateEmail, validatePassword } from "../utils/validators";
import AuthLayout from "../components/AuthLayout";
import { ArrowLeft } from 'lucide-react';

export default function Login({
  onForgotPassword,
  onRegister,
  onLoginSuccess,
  onBack
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    const newErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };

    setErrors(newErrors);
    if (newErrors.email || newErrors.password) return;

    setLoading(true);
    setError("");

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (signInError) throw signInError;
      
      if (data?.user) {
        onLoginSuccess(data.user);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Invalid email or password");
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      });

      if (oauthError) throw oauthError;
      
      // Supabase redirects automatically - no need to do anything here
      
    } catch (err) {
      console.error("Google OAuth error:", err);
      setError("Failed to connect to Google. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      headerLeft={
        onBack && (
          <button
            onClick={onBack}
            type="button"
            className="flex items-center gap-2 text-slate-600 hover:text-[#06402B] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm font-medium">Back to browsing</span>
          </button>
        )
      }
      headerRight={
        <div className="text-xs sm:text-sm text-gray-600">
          <span className="hidden sm:inline">Don't have an account? </span>
          <button
            onClick={onRegister}
            type="button"
            className="text-[#06402B] font-medium hover:underline"
          >
            Register<span className="hidden sm:inline"> Now</span>
          </button>
        </div>
      }
    >
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">
              Welcome back
            </h1>
            <p className="text-sm sm:text-base text-gray-500">
              Sign in to access all features
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm mb-4 sm:mb-6">
              {error}
            </div>
          )}

          <div className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="your@email.com"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-[5px] border-2 border-gray-200 outline-none text-sm sm:text-base transition-colors focus:border-[#06402B]"
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-red-500 text-[10px] sm:text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter your password"
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-[5px] border-2 ${
                  error ? 'border-red-300' : 'border-gray-200'
                } outline-none text-sm sm:text-base transition-colors focus:border-[#06402B]`}
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="text-red-500 text-[10px] sm:text-xs mt-1">{errors.password}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-[#06402B] text-xs sm:text-sm font-medium hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="button"
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-[#06402B] text-white py-3 sm:py-3.5 rounded-xl sm:rounded-[5px] font-semibold text-sm sm:text-base hover:bg-[#053527] transition-colors disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="relative my-4 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-3 sm:px-4 bg-[#FBFCF6] text-gray-500">or continue with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl sm:rounded-[5px] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 sm:gap-3 active:scale-[0.98] disabled:opacity-50"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
              <span className="text-sm sm:text-base font-medium text-gray-700">Google</span>
            </button>

            <p className="text-center text-xs sm:text-sm text-gray-600 sm:hidden pt-2">
              Don't have an account?{' '}
              <button
                onClick={onRegister}
                type="button"
                className="text-[#06402B] font-semibold hover:underline"
              >
                Register Now
              </button>
            </p>

            {onBack && (
              <p className="text-center text-xs text-gray-500 pt-2">
                <button
                  onClick={onBack}
                  type="button"
                  className="hover:underline"
                >
                  Continue browsing as guest →
                </button>
              </p>
            )}
          </div>
        </div>
      </main>
    </AuthLayout>
  );
}